/* Order-style strategies (Custom requests, Voice & video calls): the mode, a
   priced table and the delivery/availability text; max price drops and the
   guidance prompts live under Advanced. */

import { useTranslation } from 'react-i18next';
import type { CallPolicyBody, CustomPolicyBody } from '../../../../api/client';
import { defaultCustomPricings } from '../../CustomRequests/defaults';
import { defaultCallPricings } from '../../VoiceCalls/defaults';
import { AdvancedToggle, Btn, Card, Empty, Foot, NumInput, Row, Section, Seg, Stack, TextArea, TextInput, THead, TRow, XButton } from '../kit';
import { C } from '../tokens';
import type { Patch } from './TipStrategies';
import { callRowsFrom, customRowsFrom, nid, type CallRow, type CustomRow } from './orderRows';

type Mode = 'automate' | 'handoff' | 'decline';

/* ── Shared bits ────────────────────────────────────────────────── */

function ModeCard({ call, mode, onMode, note, onNote }: { call: boolean; mode: Mode; onMode: (m: Mode) => void; note: string | null | undefined; onNote: (v: string | null) => void }) {
  const { t } = useTranslation('assistantCompact');
  const k = call ? 'call' : 'custom';
  return (
    <Card>
      <Row label={t(`mon.order.ask.${k}`)} help={t(`mon.order.mode.${k}.${mode}`)}>
        <Seg value={mode} onChange={onMode} options={[
          { id: 'automate', label: t('mon.order.automate') },
          { id: 'handoff', label: t('mon.order.handoff') },
          { id: 'decline', label: t('mon.order.decline') },
        ]} />
      </Row>
      {mode === 'decline' && (
        <Row stack label={t('mon.order.declineNote')} help={t('mon.order.declineNoteHelp')}>
          <TextArea value={note ?? ''} onChange={(v) => onNote(v || null)} placeholder={t('mon.order.declineNotePh')} height={52} />
        </Row>
      )}
    </Card>
  );
}

function OrderAdvanced({ call, drops, onDrops, pricing, onPricing, strategy, onStrategy }: {
  call: boolean; drops: number; onDrops: (v: number) => void; pricing: string | null; onPricing: (v: string | null) => void; strategy: string | null; onStrategy: (v: string | null) => void;
}) {
  const { t } = useTranslation('assistantCompact');
  const k = call ? 'call' : 'custom';
  return (
    <Section title={t('mon.order.advTitle')} hint={t('mon.order.advTitleHint')}>
      <Card>
        <Row label={t('mon.order.drops')} help={t('mon.order.dropsHelp')}><NumInput value={drops} onChange={(v) => v !== null && onDrops(v)} min={0} max={5} int /></Row>
        <Row stack label={t('mon.order.pricingGuidance')} help={t(`mon.order.pricingGuidanceHint.${k}`)}>
          <TextArea value={pricing ?? ''} onChange={(v) => onPricing(v || null)} height={72} />
        </Row>
        <Row stack label={t('mon.order.strategyGuidance')} help={t(`mon.order.strategyGuidanceHint.${k}`)}>
          <TextArea value={strategy ?? ''} onChange={(v) => onStrategy(v || null)} height={84} />
        </Row>
      </Card>
    </Section>
  );
}

/* ── Custom requests ────────────────────────────────────────────── */

const TYPE_COLS = 'minmax(0,1fr) 72px 72px 26px';

export function CustomPage({ d, set, rows, setRows, adv, togAdv }: { d: CustomPolicyBody; set: Patch<CustomPolicyBody>; rows: CustomRow[]; setRows: (r: CustomRow[]) => void; adv: boolean; togAdv: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tr } = useTranslation('customRequests');
  const edit = (i: number, p: Partial<CustomRow>) => setRows(rows.map((r, k) => (k === i ? { ...r, ...p } : r)));
  return (
    <Stack>
      <ModeCard call={false} mode={d.custom_request_mode} onMode={(m) => set({ custom_request_mode: m })} note={d.decline_note} onNote={(v) => set({ decline_note: v })} />
      <Section
        title={t('mon.custom.types')}
        hint={t('mon.custom.typesHint')}
        right={<Btn kind="muted" height={26} onClick={() => setRows(customRowsFrom(defaultCustomPricings(tr)))}>{t('mon.order.loadExamples')}</Btn>}
      >
        <Card>
          <THead cols={TYPE_COLS}><span>{t('mon.custom.colType')}</span><span>{t('mon.order.colPrice')}</span><span>{t('mon.order.colFloor')}</span><span /></THead>
          {rows.map((r, i) => (
            <TRow key={r.id} cols={TYPE_COLS}>
              <TextInput value={r.name} onChange={(v) => edit(i, { name: v })} placeholder={t('mon.custom.typePh')} />
              <NumInput value={r.price} onChange={(v) => edit(i, { price: v })} min={0} max={9999} allowEmpty width="100%" />
              <NumInput value={r.floor} onChange={(v) => edit(i, { floor: v })} min={0} max={9999} allowEmpty width="100%" />
              <XButton onClick={() => setRows(rows.filter((_, k) => k !== i))} title={t('remove')} />
            </TRow>
          ))}
          {!rows.length && <Empty>{t('mon.custom.empty')}</Empty>}
          <Foot>
            <Btn kind="dashed" height={26} onClick={() => setRows([...rows, { id: nid(), name: '', price: null, floor: null }])}>{t('mon.custom.add')}</Btn>
            <span style={{ flex: 1 }} />
            <span style={{ textAlign: 'right' }}>{t('mon.custom.foot')}</span>
          </Foot>
        </Card>
      </Section>
      <Card>
        <Row label={t('mon.custom.photoEta')} help={t('mon.custom.photoEtaHelp')}><TextInput value={d.photo_delivery_eta ?? ''} onChange={(v) => set({ photo_delivery_eta: v || null })} width={220} /></Row>
        <Row label={t('mon.custom.videoEta')} help={t('mon.custom.videoEtaHelp')}><TextInput value={d.video_delivery_eta ?? ''} onChange={(v) => set({ video_delivery_eta: v || null })} width={220} /></Row>
      </Card>
      <AdvancedToggle open={adv} onToggle={togAdv} hint={t('mon.advHint.order')} />
      {adv && (
        <OrderAdvanced
          call={false}
          drops={d.max_negotiation_attempts} onDrops={(v) => set({ max_negotiation_attempts: v })}
          pricing={d.pricing_guidance} onPricing={(v) => set({ pricing_guidance: v })}
          strategy={d.strategy_prompt} onStrategy={(v) => set({ strategy_prompt: v })}
        />
      )}
    </Stack>
  );
}

/* ── Voice & video calls ────────────────────────────────────────── */

const CALL_COLS = '118px minmax(0,1fr) 72px 72px 26px';

export function CallsPage({ d, set, rows, setRows, adv, togAdv }: { d: CallPolicyBody; set: Patch<CallPolicyBody>; rows: CallRow[]; setRows: (r: CallRow[]) => void; adv: boolean; togAdv: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const { t: tc } = useTranslation('voiceCalls');
  const edit = (i: number, p: Partial<CallRow>) => setRows(rows.map((r, k) => (k === i ? { ...r, ...p } : r)));
  return (
    <Stack>
      <ModeCard call mode={d.call_request_mode} onMode={(m) => set({ call_request_mode: m })} note={d.decline_note} onNote={(v) => set({ decline_note: v })} />
      <Section
        title={t('mon.calls.prices')}
        hint={t('mon.calls.pricesHint')}
        right={<Btn kind="muted" height={26} onClick={() => setRows(callRowsFrom(defaultCallPricings(tc)))}>{t('mon.order.loadExamples')}</Btn>}
      >
        <Card>
          <THead cols={CALL_COLS}><span>{t('mon.calls.colType')}</span><span>{t('mon.calls.colLength')}</span><span>{t('mon.order.colPrice')}</span><span>{t('mon.order.colFloor')}</span><span /></THead>
          {rows.map((r, i) => (
            <TRow key={r.id} cols={CALL_COLS}>
              <div style={{ display: 'flex', gap: 2, padding: 2, background: C.input, border: `1px solid ${C.border}`, borderRadius: 6 }}>
                {(['voice', 'video'] as const).map((m) => (
                  <button key={m} type="button" onClick={() => edit(i, { modality: m })} style={{ flex: 1, height: 22, border: 0, borderRadius: 4, background: r.modality === m ? C.segActive : 'transparent', color: r.modality === m ? C.text : C.sub, font: '500 12px var(--font-sans)', cursor: 'pointer' }}>
                    {t(`mon.calls.${m}`)}
                  </button>
                ))}
              </div>
              <TextInput value={r.duration} onChange={(v) => edit(i, { duration: v })} placeholder={t('mon.calls.lengthPh')} />
              <NumInput value={r.price} onChange={(v) => edit(i, { price: v })} min={0} max={9999} allowEmpty width="100%" />
              <NumInput value={r.floor} onChange={(v) => edit(i, { floor: v })} min={0} max={9999} allowEmpty width="100%" />
              <XButton onClick={() => setRows(rows.filter((_, k) => k !== i))} title={t('remove')} />
            </TRow>
          ))}
          {!rows.length && <Empty>{t('mon.calls.empty')}</Empty>}
          <Foot>
            <Btn kind="dashed" height={26} onClick={() => setRows([...rows, { id: nid(), modality: 'voice', duration: '', price: null, floor: null }])}>{t('mon.calls.add')}</Btn>
            <span style={{ flex: 1 }} />
            <span style={{ textAlign: 'right' }}>{t('mon.calls.foot')}</span>
          </Foot>
        </Card>
      </Section>
      <Card>
        <Row stack label={t('mon.calls.avail')} help={t('mon.calls.availHelp')}>
          <TextArea value={d.call_availability ?? ''} onChange={(v) => set({ call_availability: v || null })} height={72} />
        </Row>
      </Card>
      <AdvancedToggle open={adv} onToggle={togAdv} hint={t('mon.advHint.order')} />
      {adv && (
        <OrderAdvanced
          call
          drops={d.max_negotiation_attempts} onDrops={(v) => set({ max_negotiation_attempts: v })}
          pricing={d.pricing_guidance} onPricing={(v) => set({ pricing_guidance: v })}
          strategy={d.strategy_prompt} onStrategy={(v) => set({ strategy_prompt: v })}
        />
      )}
    </Stack>
  );
}
